import type { Stream, VideoPlayer } from "@zoom/videosdk";
import {
  getEnvFromHostname,
  RuntimeEnvironmentOptions,
} from "@growtherapy/analytics";
import { LocalVideoDelegator } from "../../../../core/room/participant/media/MediaDelegator";
import { handleError } from "../../../utils/handleError";
import { logger } from "../../../../../datadog/logger";
import { videoSDKMutex } from "../../../../core/mutex";

export class ZoomLocalVideoDelegator implements LocalVideoDelegator {
  readonly kind = "video" as const;
  #stream: typeof Stream;
  #userId: number;

  constructor(userId: number, stream: typeof Stream) {
    this.#userId = userId;
    this.#stream = stream;
  }

  get deviceId() {
    return this.#stream.getActiveCamera();
  }

  get isEnabled() {
    return !!this.#stream;
  }

  async attach(element: VideoPlayer, videoQuality?: number): Promise<void> {
    return videoSDKMutex.runExclusive(() =>
      this.#attach(element, videoQuality),
    );
  }

  async #attach(element: VideoPlayer, videoQuality?: number): Promise<void> {
    this.#logDebugInfo("[ZoomLocalVideo]: Attaching video track to element", {
      element,
      videoQuality,
    });

    try {
      await this.#stream.attachVideo(this.#userId, videoQuality || 1, element);
    } catch (attachVideoError) {
      handleError("[ZoomLocalVideo]: attach", attachVideoError);
    }
  }

  async detach(element: VideoPlayer): Promise<void> {
    return videoSDKMutex.runExclusive(() => this.#detach(element));
  }

  async #detach(element: VideoPlayer): Promise<void> {
    this.#logDebugInfo("[ZoomLocalVideo]: Detaching video track from element", {
      element,
    });

    try {
      if (element) {
        // detach for the specific local video, whether thumbnail or settings
        await this.#stream.detachVideo(this.#userId, element);
      }
    } catch (detachVideoError) {
      handleError("[ZoomLocalVideo]: detach", detachVideoError);
    }
  }

  #logDebugInfo(...args: Parameters<typeof logger.debug>) {
    if (
      getEnvFromHostname({ hostname: location.hostname }) ===
      RuntimeEnvironmentOptions.local
    ) {
      const [message, context] = args;
      logger.debug(message, {
        this: {
          kind: this.kind,
          stream: this.#stream,
          userId: this.#userId,
          isEnabled: this.isEnabled,
        },
        ...context,
      });
    }
  }
}
